import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { createClientCityUseCase } from "../domain/usecases/createClientCityUseCase";

const emptyLocation = {
  name: "",
  latitude: null,
  longitude: null,
};

function CityAutocomplete({ label, value, onChange, onSelect }) {
  return (
    <PlacesAutocomplete
      value={value.name}
      onChange={(name) => onChange({ name, latitude: null, longitude: null })}
      onSelect={async (address) => {
        const results = await geocodeByAddress(address);
        const coordinates = await getLatLng(results[0]);
        onSelect({
          name: address,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        });
      }}
      searchOptions={{ types: ["(cities)"] }}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <Box sx={{ position: "relative" }}>
          <TextField
            fullWidth
            label={label}
            {...getInputProps({
              placeholder: label,
            })}
          />
          {(loading || suggestions.length > 0) && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                zIndex: 20,
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                bgcolor: "#fff",
                color: "#111827",
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {loading && (
                <Typography sx={{ px: 2, py: 1.25, color: "#111827" }}>
                  Buscando ciudad...
                </Typography>
              )}
              {suggestions.map((suggestion) => (
                <Box
                  key={suggestion.placeId || suggestion.description}
                  {...getSuggestionItemProps(suggestion)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    color: "#111827",
                    bgcolor: suggestion.active ? "#e8f3ef" : "#fff",
                    "&:hover": {
                      bgcolor: "#e8f3ef",
                    },
                  }}
                >
                  <Typography sx={{ color: "#111827" }}>
                    {suggestion.description}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      )}
    </PlacesAutocomplete>
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

function createPayload({ customName, location }) {
  return {
    name: customName.trim().toUpperCase(),
    latitude: Number(location.latitude),
    longitude: Number(location.longitude),
  };
}

function isFormReady({ customName, location }) {
  return (
    customName.trim() &&
    typeof location.latitude === "number" &&
    !Number.isNaN(location.latitude) &&
    typeof location.longitude === "number" &&
    !Number.isNaN(location.longitude)
  );
}

export const TarifaClientCityCreate = ({ onCancel, onCreated }) => {
  const [location, setLocation] = useState(emptyLocation);
  const [customName, setCustomName] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });
  const canSubmit = useMemo(
    () => isFormReady({ customName, location }),
    [customName, location],
  );

  const resetForm = () => {
    setLocation(emptyLocation);
    setCustomName("");
  };

  const handleCreate = async () => {
    const payload = createPayload({ customName, location });

    try {
      setSaving(true);
      const response = await createClientCityUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la ciudad.");
      }

      resetForm();
      onCreated?.(response);
      setDialog({
        open: true,
        type: "success",
        title: "Ciudad creada",
        message: "Ciudad creada correctamente.",
      });
    } catch (error) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: error.message || "No se pudo crear la ciudad.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight={800}>
          Crear Ciudad
        </Typography>

        <CityAutocomplete
          label="Ciudad"
          value={location}
          onChange={setLocation}
          onSelect={setLocation}
        />

        <TextField
          fullWidth
          label="Nombre Personalizado"
          value={customName}
          onChange={(event) => setCustomName(event.target.value.toUpperCase())}
          inputProps={{ style: { textTransform: "uppercase" } }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleCreate}
            disabled={!canSubmit || saving}
            sx={{
              bgcolor: "#165c53",
              "&:hover": {
                bgcolor: "#12483f",
              },
            }}
          >
            {saving ? "Creando..." : "Crear"}
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={dialog.open}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
      >
        <DialogTitle>{dialog.title}</DialogTitle>
        <DialogContent>
          <Alert severity={dialog.type}>{dialog.message}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog((prev) => ({ ...prev, open: false }))}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TarifaClientCityCreate;
